import { formatBarExperience } from "@/lib/format-experience";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAutoRefresh } from "@/hooks/use-auto-refresh";
import { useRealtimeTables } from "@/hooks/use-realtime-tables";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { dismissFromDashboardPatch, isHiddenFromDashboard } from "@/lib/venue-dismissal";
import { postShift, decideApplication, updateShiftTimes, rescheduleShift, confirmArrival, setShiftListingClosed } from "@/lib/shifts.functions";
import { logBartenderArrival } from "@/lib/arrival.functions";
import { getBartenderStats } from "@/lib/bartender-stats.functions";
import { getApplicantProfiles } from "@/lib/applicant-profiles.functions";
import { toggleFavoriteBartender } from "@/lib/favorites.functions";
import { submitReview } from "@/lib/reviews.functions";
import { confirmShiftCompletion, cancelShift, listOutstandingLateFees } from "@/lib/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { flowBreadcrumb, setFlowSubject } from "@/lib/breadcrumbs";
import { reportCancellationFailure } from "@/lib/cancellation-error";
import { ShiftCheckout } from "@/components/ShiftCheckout";
import { LateFeeCheckout } from "@/components/LateFeeCheckout";
import { ReserveHeader } from "@/components/ReserveHeader";
import { SupportRequestsSection } from "@/components/SupportRequestsSection";
import { InviteBartendersCard } from "@/components/InviteBartendersCard";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CancelShiftDialog } from "@/components/CancelShiftDialog";
import { ShiftTimePicker } from "@/components/ShiftTimePicker";
import { CallBartenderButton } from "@/components/CallBartenderButton";
import { TipAllocationDialog } from "@/components/TipAllocationDialog";
import type { CancelReason } from "@/components/CancelShiftDialog";
import { StrikesPanel } from "@/components/StrikesPanel";
import { ExportReportButton } from "@/components/ExportReportButton";
import { ShiftTimelineButton } from "@/components/ShiftTimelineButton";
import { ApplicantAvatar } from "@/components/ApplicantAvatar";
import { FillConfidenceMeter } from "@/components/FillConfidenceMeter";

import { ApplicantChatButton } from "@/components/ApplicantChatButton";
import { ViewCvButton } from "@/components/ViewCvButton";
import { MutualFinishDialog } from "@/components/MutualFinishDialog";
import { GrossMisconductDialog } from "@/components/GrossMisconductDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { MutualFinishBanner } from "@/components/MutualFinishBanner";
import { PAY_RATES, PLATFORM_FEE_TIERS as DEFAULT_FEE_TIERS, PLATFORM_FEE_PENCE, tieredPlatformFeePence as defaultTieredFee, type ShiftRole } from "@/lib/pay-rates";
import type { CancellationInput } from "@/lib/cancellation-policy";
import { noShowClosesAtMs, noShowOpensAtMs } from "@/lib/no-show-window";
import { CREDIT_UNIT_PENCE, creditAppliedPence, creditUnits } from "@/lib/venue-credits";

import { listFeeTiers } from "@/lib/fee-tiers.functions";
import { SaveAsTemplateButton } from "@/components/SaveAsTemplateButton";
import { ApplicantComparisonDialog } from "@/components/ApplicantComparisonDialog";
import { toast } from "sonner";

type ShiftStatus = "open" | "awaiting_payment" | "paid" | "filled" | "completed" | "cancelled" | "disputed";

type Shift = {
  id: string;
  start_time: string;
  end_time: string;
  hourly_rate: number;
  role_type: string;
  requires_dbs: boolean;
  status: ShiftStatus;
  total_cost: number | null;
  notes: string | null;
  bartender_id: string | null;
  payment_status: string;
  venue_confirmed_at: string | null;
  bartender_confirmed_at: string | null;
  address: string | null;
  postcode: string | null;
  favorites_only_until: string | null;
  positions_needed: number;
  tip_payment_status: "pending" | "paid" | "not_applicable" | null;
  tip_amount_pence: number | null;
  listing_closed_at: string | null;
  venue_dismissed_at?: string | null;
};

type BartenderLite = {
  id: string;
  full_name: string | null;
  cv_file_path: string | null;
  about_me: string | null;
  is_verified: boolean;
  has_dbs: boolean;
  avatar_path: string | null;
  /** Capability tags pulled from the bartender's CV by the parser. */
  skills?: string[];
  total_months_experience?: number | null;
};

type BartenderStats = {
  avg_rating: number | null;
  completed_count: number;
};

type Application = {
  id: string;
  shift_id: string;
  bartender_id: string;
  status: "pending" | "accepted" | "denied";
  created_at: string;
  arrival_venue_confirmed_at: string | null;
  arrival_bartender_confirmed_at: string | null;
  actual_arrival_at: string | null;
  late_deduction_pence: number | null;
};


export const Route = createFileRoute("/_authenticated/venue/")({
  head: () => ({ meta: [{ title: "Venue Register — The Hospitality Reserve" }] }),
  component: VenuePage,
});

function VenuePage() {
  const navigate = useNavigate();
  const [accountRole, setAccountRole] = useState<string | null | undefined>(undefined);
  const [venueUid, setVenueUid] = useState<string | null>(null);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [bartenders, setBartenders] = useState<Record<string, BartenderLite>>({});
  const [bartenderStats, setBartenderStats] = useState<Record<string, BartenderStats>>({});
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [favTogglingId, setFavTogglingId] = useState<string | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [step, setStep] = useState(1);
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [payingShiftId, setPayingShiftId] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelShiftId, setCancelShiftId] = useState<string | null>(null);
  const [autoReviewId, setAutoReviewId] = useState<string | null>(null);
  const [form, setForm] = useState({ start_time: "", end_time: "", role_type: "bartender", requires_dbs: false, notes: "", address: "", postcode: "", favorites_early_access_hours: "0", positions_needed: "1", break_acknowledged: false, break_is_paid: true, hourly_rate: "", is_asap: false, use_referral_waiver: false, contact_name: "", contact_phone: "", contact_job_title: "" });
  const [lateFees, setLateFees] = useState<Array<{ id: string; shift_id: string; total_pence: number; amount_pence: number; status: string; created_at: string }>>([]);
  const [payingFeeId, setPayingFeeId] = useState<string | null>(null);
  const [creditPence, setCreditPence] = useState(0);
  const [waiversRemaining, setWaiversRemaining] = useState(0);
  const [feeTiers, setFeeTiers] = useState<Array<{ min: number; max: number | null; fee_pence: number }>>(
    DEFAULT_FEE_TIERS.map((t) => ({ min: t.min, max: t.max, fee_pence: t.fee_pence })),
  );

  const post = useServerFn(postShift);
  const decide = useServerFn(decideApplication);
  const fetchStats = useServerFn(getBartenderStats);
  const fetchApplicantProfiles = useServerFn(getApplicantProfiles);
  const toggleFav = useServerFn(toggleFavoriteBartender);
  const confirm = useServerFn(confirmShiftCompletion);
  const cancel = useServerFn(cancelShift);
  const fetchLateFees = useServerFn(listOutstandingLateFees);
  const fetchTiers = useServerFn(listFeeTiers);

  const tieredPlatformFeePence = (n: number) => {
    const positions = Math.max(1, Math.floor(n || 1));
    const match = feeTiers.find((t) => positions >= t.min && (t.max === null || positions <= t.max));
    return match ? match.fee_pence : defaultTieredFee(positions);
  };
  const PLATFORM_FEE_TIERS = feeTiers;

  // Role guard: this dashboard is venue-only. A bartender/admin session landing
  // here would otherwise only discover the mismatch when posting fails server-side.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return;
      const { data } = await supabase.from("profiles").select("role").eq("id", auth.user.id).maybeSingle();
      if (cancelled) return;
      const role = (data as { role?: string } | null)?.role ?? null;
      setAccountRole(role);
      setVenueUid(auth.user.id);
      if (role === "bartender") void navigate({ to: "/bartender", replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void fetchTiers().then((r) => {
      const rows = (r.tiers ?? []).map((t) => ({ min: t.min_workers, max: t.max_workers, fee_pence: t.fee_pence }));
      if (rows.length) setFeeTiers(rows);
    }).catch(() => { /* keep defaults */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    const { data } = await supabase
      .from("shifts")
      .select("id,start_time,end_time,hourly_rate,role_type,requires_dbs,status,total_cost,notes,bartender_id,payment_status,venue_confirmed_at,bartender_confirmed_at,address,postcode,favorites_only_until,positions_needed,tip_payment_status,tip_amount_pence,listing_closed_at,venue_dismissed_at")
      .order("start_time", { ascending: true });
    const list = (data ?? []) as Shift[];
    setShifts(list);

    const shiftIds = list.map((s) => s.id);
    const { data: apps } = shiftIds.length
      ? await supabase
          .from("bookings")
          .select("id,shift_id,bartender_id,status,created_at,arrival_venue_confirmed_at,arrival_bartender_confirmed_at,actual_arrival_at,late_deduction_pence")
          .in("shift_id", shiftIds)
          .order("created_at", { ascending: true })
      : { data: [] };
    const appList = (apps ?? []) as Application[];
    setApplications(appList);

    const bartenderIds = Array.from(new Set([
      ...list.map((s) => s.bartender_id).filter(Boolean) as string[],
      ...appList.map((a) => a.bartender_id),
    ]));
    if (bartenderIds.length) {
      // Applicant profiles are not directly readable by venues (privacy rules),
      // so fetch the presentable fields server-side for our own shifts.
      let map: Record<string, BartenderLite> = {};
      try {
        map = (await fetchApplicantProfiles({ data: { shift_ids: shiftIds } })) as Record<string, BartenderLite>;
      } catch {
        map = {};
      }
      setBartenders(map);

      try {
        const stats = await fetchStats({ data: { bartender_ids: bartenderIds } });
        setBartenderStats(stats);
      } catch {
        setBartenderStats({});
      }
    }

    const { data: favs } = await supabase
      .from("favorite_bartenders")
      .select("bartender_id");
    setFavorites(new Set((favs ?? []).map((f) => f.bartender_id as string)));

    if (shiftIds.length) {
      const { data: revs } = await supabase.from("reviews").select("shift_id").in("shift_id", shiftIds);
      setReviewedIds(new Set((revs ?? []).map((r) => r.shift_id)));
    }
    try {
      const fees = await fetchLateFees();
      setLateFees(fees as typeof lateFees);
    } catch { setLateFees([]); }
    try {
      const { data: s2 } = await supabase.auth.getSession();
      const uid2 = s2.session?.user?.id;
      if (uid2) {
        const { data: pc } = await supabase.from("profiles").select("credit_pence, venue_fee_waivers_remaining").eq("id", uid2).maybeSingle();
        setCreditPence(Number((pc as { credit_pence?: number } | null)?.credit_pence ?? 0));
        const remaining = Number((pc as { venue_fee_waivers_remaining?: number } | null)?.venue_fee_waivers_remaining ?? 0);
        setWaiversRemaining(remaining);
        // Auto-apply an earned waiver by default whenever the venue has any left.
        // Never touch the form during a silent background refresh — the venue
        // may be mid-way through posting a shift.
        if (remaining > 0 && !opts?.silent) setForm((f) => ({ ...f, use_referral_waiver: true }));

      }
    } catch { /* ignore */ }
    setLoading(false);
  };



  useEffect(() => {
    (async () => {
      // getSession() reads the in-memory/localStorage session — instant.
      // getUser() round-trips to the auth server, which can hang the first
      // render right after login and produces a white screen until refresh.
      const { data: s } = await supabase.auth.getSession();
      const uid = s.session?.user?.id;
      if (!uid) return;
      const { data: p } = await supabase.from("profiles").select("onboarding_status").eq("id", uid).maybeSingle();
      const status = (p as { onboarding_status?: string } | null)?.onboarding_status;
      if (status && status !== "approved") {
        window.location.replace("/venue-pending");
        return;
      }
      await load();
    })();
  }, []);

  // Live dashboard: silently re-fetch shifts/applications every 20s while the
  // tab is visible (and on refocus) so new interest or accepted shifts show up
  // without a manual page refresh.
  // Paused while the venue is part-way through the post-a-shift wizard so
  // nothing they've typed can be disturbed.
  useAutoRefresh(() => load({ silent: true }), { enabled: step === 1, intervalMs: 60000 });

  // ---- Live updates (Supabase Realtime) -------------------------------------
  // Instant instead of polling. Still paused mid-wizard so nothing typed is
  // disturbed while a shift is being composed.
  const reloadTimer = useRef<number | null>(null);
  const scheduleReload = () => {
    if (typeof window === "undefined") return;
    if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(() => { void load({ silent: true }); }, 400);
  };
  const shiftStatusRef = useRef<Map<string, string>>(new Map());
  useEffect(() => { shiftStatusRef.current = new Map(shifts.map((s) => [s.id, s.status])); }, [shifts]);

  useRealtimeTables(
    "venue-dashboard",
    venueUid ? [{ table: "shifts", filter: `venue_id=eq.${venueUid}` }, { table: "bookings" }] : [],
    (ev) => {
      const row = (ev.new ?? ev.old) as { id?: string; status?: string } | null;
      if (ev.table === "shifts" && row?.id && ev.eventType === "UPDATE") {
        const prev = shiftStatusRef.current.get(row.id);
        if (row.status && prev && prev !== row.status) {
          toast(`A shift is now ${String(row.status).replace(/_/g, " ")}`);
        }
      }
      if (ev.table === "bookings" && ev.eventType === "INSERT") {
        toast("Someone just registered interest in one of your shifts");
      }
      scheduleReload();
    },
    !!venueUid && step === 1,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const id = new URLSearchParams(window.location.search).get("review");
    if (id) setAutoReviewId(id);
  }, []);

  const handleToggleFavorite = async (bartenderId: string) => {
    setFavTogglingId(bartenderId);
    try {
      const r = await toggleFav({ data: { bartender_id: bartenderId } });
      setFavorites((prev) => {
        const next = new Set(prev);
        if (r.favorited) next.add(bartenderId); else next.delete(bartenderId);
        return next;
      });
      toast.success(r.favorited ? "Added to favorites." : "Removed from favorites.");
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not update favorites."); }
    finally { setFavTogglingId(null); }
  };

  const handlePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(["bartender", "bar_back", "mixologist"] as const).includes(form.role_type as "bartender")) {
      toast.error("Please choose a role."); return;
    }
    const rawPos = form.positions_needed.trim();
    if (!/^\d+$/.test(rawPos)) {
      toast.error("Staff count must be a whole number between 1 and 50.");
      return;
    }
    const positions = Math.floor(Number(rawPos));
    if (!Number.isFinite(positions) || positions < 1 || positions > 50) {
      toast.error("Staff count must be between 1 and 50.");
      return;
    }
    // UK Working Time Regs: mirror the server rule so the wizard never hard-errors.
    const sMs = new Date(form.start_time).getTime();
    const eMs = new Date(form.end_time).getTime();
    const shiftHours = Number.isFinite(sMs) && Number.isFinite(eMs) && eMs > sMs ? (eMs - sMs) / 3_600_000 : 0;
    if (shiftHours > 6 && !form.break_acknowledged) {
      setStep(4);
      toast.error("Shifts over 6 hours need the 20-minute rest break confirmed — tick the box in Review.");
      return;
    }
    setPosting(true);
    try {
      await post({
        data: {
          start_time: new Date(form.start_time).toISOString(),
          end_time: new Date(form.end_time).toISOString(),
          role_type: form.role_type as "bartender" | "bar_back" | "mixologist",
          requires_dbs: form.requires_dbs,
          notes: form.notes || null,
          address: form.address.trim(),
          postcode: form.postcode.trim(),
          contact_name: form.contact_name.trim(),
          contact_phone: form.contact_phone.trim(),
          contact_job_title: form.contact_job_title.trim(),
          favorites_early_access_hours: Math.max(0, Math.min(168, Number(form.favorites_early_access_hours) || 0)),
          positions_needed: positions,
          break_acknowledged: form.break_acknowledged,
          break_is_paid: form.break_is_paid,
          hourly_rate_override: form.hourly_rate.trim() ? Number(form.hourly_rate) : undefined,
          is_asap: form.is_asap,
          use_referral_waiver: form.use_referral_waiver,
        },

      });
      toast.success("Engagement posted to the register.");
      setForm({ start_time: "", end_time: "", role_type: "bartender", requires_dbs: false, notes: "", address: "", postcode: "", favorites_early_access_hours: "0", positions_needed: "1", break_acknowledged: false, break_is_paid: true, hourly_rate: "", is_asap: false, use_referral_waiver: false, contact_name: "", contact_phone: "", contact_job_title: "" });
      setStep(1);
      await load();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not post engagement."); }
    finally { setPosting(false); }
  };

  const handleConfirm = async (id: string) => {
    setActingId(id);
    try {
      const res = await confirm({ data: { shift_id: id } });
      toast.success(res.completed ? "Confirmed — engagement closed." : "Confirmed. Awaiting bartender confirmation.");
      await load();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not confirm."); }
    finally { setActingId(null); }
  };

  // A listing with no currently-accepted bartender is always free to cancel —
  // a bartender who accepted and then dropped out leaves the listing unfilled.
  const cancelHasConfirmedStaff = (() => {
    if (!cancelShiftId) return false;
    return applications.some((a) => a.shift_id === cancelShiftId && a.status === "accepted");
  })();

  // Exact policy (free / half-pay / refund) for the shift being cancelled.
  const cancelPolicy: CancellationInput | null = (() => {
    const s = cancelShiftId ? shifts.find((x) => x.id === cancelShiftId) : null;
    if (!s) return null;
    const acceptedCount = applications.filter((a) => a.shift_id === s.id && a.status === "accepted").length;

    const positions = s.positions_needed || 1;
    const hours = (new Date(s.end_time).getTime() - new Date(s.start_time).getTime()) / 3_600_000;
    const wagePence = Math.round(Number(s.hourly_rate) * hours * positions * 100);
    const feePence = PLATFORM_FEE_PENCE * positions;
    return {
      isVenue: true,
      acceptedCount,
      hoursToStart: (new Date(s.start_time).getTime() - Date.now()) / 3_600_000,
      hasCharge: s.payment_status === "paid",
      wagePence,
      feePence,
      totalPence: s.total_cost != null ? Math.round(Number(s.total_cost) * 100) : wagePence + feePence,
    };
  })();


  const handleCancel = (id: string) => {
    setCancelShiftId(id);
    setCancelDialogOpen(true);
  };
  // Clears a finished job from the dashboard. It stays in Previous Engagements.
  const handleDismiss = async (id: string) => {
    setShifts((prev) => prev.map((s) => (s.id === id ? { ...s, venue_dismissed_at: new Date().toISOString() } : s)));
    const { error } = await supabase
      .from("shifts")
      .update(dismissFromDashboardPatch())
      .eq("id", id);
    if (error) {
      toast.error("Could not clear that job.");
      await load();
      return;
    }
    toast.success("Cleared from your dashboard.");
  };
  const doCancel = async (reason: CancelReason, reason_text: string) => {
    if (!cancelShiftId) return;
    setCancelDialogOpen(false);
    setActingId(cancelShiftId);
    setFlowSubject({ shiftId: cancelShiftId });
    flowBreadcrumb("cancellation", "cancel requested", { shift_id: cancelShiftId, role: "venue", reason });
    try {
      const res = await cancel({ data: { shift_id: cancelShiftId, environment: getStripeEnvironment(), reason, reason_text: reason_text || undefined } });
      if ("error" in res) throw new Error(res.error);
      if (res.suspended) {
        toast.error("Strike 3 — your account has been suspended. Submit an appeal below.");
      } else if (res.strike_issued && res.strike_number === 2) {
        toast.warning("Strike 2 — FINAL WARNING. Next late cancellation will suspend your account.");
      } else if (res.strike_issued) {
        toast.warning(`Strike 1 logged. £${(res.refund_pence / 100).toFixed(2)} refunded.`);
      } else {
        toast.success(res.refund_pence > 0 ? `Cancelled. £${(res.refund_pence / 100).toFixed(2)} refunded.` : "Cancelled. Nothing was charged.");
      }
      await load();
    } catch (err) {
      const t = reportCancellationFailure(err, { shift_id: cancelShiftId, role: "venue", reason });
      toast.error(t.title, { description: t.description, duration: 12000 });
    }
    finally { setActingId(null); setCancelShiftId(null); }
  };

  if (accountRole !== undefined && accountRole !== "venue" && accountRole !== "admin") {
    return (
      <div className="min-h-screen bg-background">
        <ReserveHeader role="venue" />
        <main className="mx-auto max-w-xl px-4 py-16 text-center">
          <p className="tracking-wide-luxury text-accent text-xs uppercase">Wrong dashboard</p>
          <h1 className="mt-3 font-display text-3xl">This area is for venues</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            You're signed in with a bartender account, so you can't post shifts here. Head to your shift feed to browse and apply for work.
          </p>
          <Link
            to="/bartender"
            className="mt-6 inline-block rounded-xl bg-foreground px-6 py-3 text-xs tracking-wide-luxury uppercase text-background"
          >
            Go to my dashboard
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="venue" />
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6">
        <InviteBartendersCard />
        <div className="mt-4">
          <SupportRequestsSection />
        </div>
      </div>
      <main className="mx-auto max-w-7xl px-4 py-8 grid gap-10 sm:px-6 sm:py-10 lg:grid-cols-[420px_1fr]">
        <section>

          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <p className="tracking-wide-luxury text-accent">New Engagement</p>
              <h1 className="mt-2 font-display text-3xl tracking-tight sm:text-4xl">Post a Shift</h1>
            </div>
            <Link to="/venue/series" className="border border-border px-3 py-2 text-xs tracking-wide-luxury hover:border-accent">
              Recurring Series
            </Link>
          </div>
          <span className="gold-divider mt-4" />

          {lateFees.length > 0 && (
            <div className="mt-6 border border-red-700/40 bg-red-900/10 p-5">
              <p className="tracking-wide-luxury text-red-400 text-xs">Outstanding Late Cancellation Fee{lateFees.length > 1 ? "s" : ""}</p>
              <h3 className="mt-2 font-display text-xl text-foreground">Payment required before posting new jobs</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                You cancelled an engagement within 24 hours of its start time. A 50% fee of the engagement's
                total cost is due. New shifts cannot be posted until all outstanding fees are settled.
              </p>
              <ul className="mt-4 space-y-3">
                {lateFees.map((f) => (
                  <li key={f.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-red-700/30 pt-3">
                    <div className="text-sm">
                      <div className="text-foreground">Fee: £{(f.amount_pence / 100).toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground">
                        Total cost £{(f.total_pence / 100).toFixed(2)} · raised {new Date(f.created_at).toLocaleDateString("en-GB")}
                      </div>
                    </div>
                    <Button
                      onClick={() => setPayingFeeId(f.id)}
                      className="rounded-none bg-red-600 text-white hover:bg-red-700 tracking-wide-luxury"
                    >
                      Pay £{(f.amount_pence / 100).toFixed(2)}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {creditPence > 0 && (
            <div className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-accent/30 bg-accent/5 p-4 shadow-[0_8px_24px_-20px_rgba(0,0,0,0.5)]">
              <div>
                <p className="tracking-wide-luxury text-[10px] uppercase text-accent">Booking credit</p>
                <p className="text-lg font-serif">
                  {creditUnits(creditPence)} × £{(CREDIT_UNIT_PENCE / 100).toFixed(2)} credit{creditUnits(creditPence) === 1 ? "" : "s"} left
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  £{(creditPence / 100).toFixed(2)} total · one credit is applied automatically to this booking.
                </p>
              </div>
              <div className="flex shrink-0 gap-1" aria-hidden="true">
                {Array.from({ length: Math.min(6, creditUnits(creditPence)) }).map((_, i) => (
                  <span key={i} className="h-2.5 w-2.5 rounded-full bg-accent" />
                ))}
              </div>
            </div>
          )}



          {(() => {
            const rawPos = form.positions_needed.trim();
            const posValid = /^\d+$/.test(rawPos) && Number(rawPos) >= 1 && Number(rawPos) <= 50;
            const breakBlocked = (() => {
              const s2 = new Date(form.start_time).getTime();
              const e2 = new Date(form.end_time).getTime();
              const h = Number.isFinite(s2) && Number.isFinite(e2) && e2 > s2 ? (e2 - s2) / 3_600_000 : 0;
              return h > 6 && !form.break_acknowledged;
            })();
            const timesReady = !!form.start_time && !!form.end_time && new Date(form.end_time) > new Date(form.start_time);
            const contactReady =
              form.contact_name.trim().length > 1 &&
              /^[0-9+()\-\s]{7,32}$/.test(form.contact_phone.trim()) &&
              form.contact_job_title.trim().length > 1;
            const placeReady =
              form.address.trim().length > 2 &&
              form.postcode.trim().length > 4 &&
              form.favorites_early_access_hours.trim() !== "" &&
              contactReady;
            const stepReady = step === 1 ? timesReady : step === 2 ? posValid : step === 3 ? placeReady : true;
            const STEPS = ["When", "Team", "Place", "Review"];

            return (
          <form onSubmit={handlePost} className="mt-6 rounded-2xl border border-border/60 bg-card p-5 shadow-[0_8px_30px_-18px_rgba(0,0,0,0.35)] sm:p-6">

            {/* Step rail */}
            <div className="flex items-center gap-2">
              {STEPS.map((label, i) => {
                const n = i + 1;
                const done = n < step;
                const active = n === step;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => { if (n < step) setStep(n); }}
                    className={`flex-1 rounded-full px-2 py-1.5 text-[0.6rem] uppercase tracking-wide-luxury transition-colors ${
                      active
                        ? "bg-foreground text-background"
                        : done
                          ? "bg-accent/15 text-accent"
                          : "bg-muted/60 text-muted-foreground"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Live-updates status: paused while composing so nothing typed is disturbed */}
            <p
              aria-live="polite"
              className={`mt-3 flex items-center gap-2 text-[0.65rem] tracking-wide-luxury uppercase ${
                step === 1 ? "text-accent" : "text-muted-foreground"
              }`}
            >
              <span
                aria-hidden="true"
                className={`inline-block h-1.5 w-1.5 rounded-full ${step === 1 ? "bg-accent animate-pulse" : "bg-muted-foreground/60"}`}
              />
              {step === 1
                ? "Live updates on"
                : "Live updates paused while you post — they resume when you finish or cancel"}
            </p>


            <div className="mt-5 space-y-5">
            {step === 1 && (
              <>
            {/* Bright red ASAP toggle — get here now */}
            {form.is_asap ? (
              <div className="flex items-stretch overflow-hidden rounded-2xl bg-red-600 text-white shadow-[0_10px_30px_-16px_rgba(220,38,38,0.9)]">
                <div className="flex-1 px-4 py-3">
                  <p className="text-xs tracking-wide-luxury uppercase opacity-90">ASAP — get here now</p>
                  <p className="text-sm mt-0.5">Pay starts when both parties confirm arrival at the venue.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, is_asap: false, start_time: "", end_time: "" }))}
                  className="px-4 text-xs tracking-wide-luxury uppercase border-l border-white/30 hover:bg-red-700"
                  aria-label="Cancel ASAP"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  const pad = (n: number) => String(n).padStart(2, "0");
                  const toLocal = (d: Date) =>
                    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                  // Earliest legal start (must be in future); pay is billed from arrival anyway.
                  const start = new Date(Date.now() + 15 * 60 * 1000);
                  start.setSeconds(0, 0);
                  const end = new Date(start.getTime() + 4 * 60 * 60 * 1000);
                  setForm((f) => ({ ...f, is_asap: true, start_time: toLocal(start), end_time: toLocal(end) }));
                }}
                className="w-full rounded-2xl bg-red-600 py-4 text-sm font-semibold uppercase tracking-wide-luxury text-white shadow-[0_10px_30px_-16px_rgba(220,38,38,0.9)] transition-transform active:scale-[0.98] hover:bg-red-700"
              >
                🚨 ASAP — Get here now
              </button>
            )}

            {form.is_asap ? (
              <div className="space-y-2">
                <Label className="tracking-wide-luxury text-xs text-muted-foreground">Shift ends at</Label>
                <input
                  type="datetime-local"
                  required
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  className="w-full rounded-xl border border-border bg-background px-3 py-3 text-sm outline-none transition focus:border-accent focus:ring-4 focus:ring-accent/15"
                />
                <p className="text-[11px] text-muted-foreground">
                  No fixed start — pay is calculated from the moment both you and the bartender confirm arrival, through to this end time.
                </p>
                <input type="hidden" name="start_time" value={form.start_time} readOnly />
              </div>
            ) : (
              <ShiftTimePicker
                startValue={form.start_time}
                endValue={form.end_time}
                onChange={(v) => setForm({ ...form, ...v })}
              />
            )}
              </>
            )}

            {step === 2 && (
              <>
            <RolePicker
              value={form.role_type}
              onChange={(v) => setForm({ ...form, role_type: v, hourly_rate: "" })}
            />
            {(() => {
              const role = (["bartender", "bar_back", "mixologist"] as const).includes(form.role_type as ShiftRole)
                ? PAY_RATES[form.role_type as ShiftRole]
                : PAY_RATES.bartender;
              const minRate = role.venue_pence / 100;
              return (
                <div>
                  <Label htmlFor="hourly_rate">Hourly Rate (£)</Label>
                  <Input
                    id="hourly_rate"
                    type="number"
                    min={minRate}
                    step="0.50"
                    inputMode="decimal"
                    placeholder={minRate.toFixed(2)}
                    value={form.hourly_rate}
                    onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                    className="mt-2 h-12 rounded-xl text-base"
                  />
                  <p className="mt-2 text-xs text-muted-foreground">
                    Minimum for {role.label} is <strong>£{minRate.toFixed(2)}/hr</strong>. Leave blank to post at the minimum, or enter a higher figure to attract bartenders faster when you're short on time.
                  </p>
                </div>
              );
            })()}

            <div>
              <Label htmlFor="positions">Staff Needed</Label>
              {(() => {
                const raw = form.positions_needed;
                const trimmed = raw.trim();
                const parsed = Number(trimmed);
                const isEmpty = trimmed === "";
                const isInteger = /^\d+$/.test(trimmed);
                const inRange = Number.isFinite(parsed) && parsed >= 1 && parsed <= 50;
                const isValid = !isEmpty && isInteger && inRange;
                let errorMsg: string | null = null;
                if (isEmpty) errorMsg = "Enter how many bar staff you need.";
                else if (!isInteger) errorMsg = "Staff count must be a whole number (no decimals).";
                else if (parsed < 1) errorMsg = "You need at least 1 bartender.";
                else if (parsed > 50) errorMsg = "Maximum 50 staff per shift. Contact us for larger events.";

                const positions = isValid ? Math.floor(parsed) : 1;
                const feePer = tieredPlatformFeePence(positions) / 100;
                const setPositions = (n: number) => {
                  const clamped = Math.max(1, Math.min(50, Math.floor(n)));
                  setForm({ ...form, positions_needed: String(clamped) });
                };
                return (
                  <div className="mt-2 space-y-3">
                    <div className="flex items-stretch gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label="Decrease staff count"
                        onClick={() => setPositions((isValid ? positions : 1) - 1)}
                        disabled={isValid && positions <= 1}
                        className="h-12 w-12 rounded-xl text-lg active:scale-95"
                      >−</Button>
                      <Input
                        id="positions"
                        type="number"
                        inputMode="numeric"
                        min="1"
                        max="50"
                        step="1"
                        aria-invalid={!isValid}
                        aria-describedby={errorMsg ? "positions-error" : undefined}
                        value={form.positions_needed}
                        onChange={(e) => setForm({ ...form, positions_needed: e.target.value })}
                        onBlur={(e) => {
                          // On blur, clamp valid-but-out-of-range or coerce decimals
                          const v = Number(e.target.value);
                          if (Number.isFinite(v) && /^\d*\.?\d+$/.test(e.target.value.trim())) {
                            setPositions(v);
                          }
                        }}
                        className={`h-12 flex-1 rounded-xl text-center text-lg font-semibold ${!isValid ? "border-destructive focus-visible:ring-destructive/40" : ""}`}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label="Increase staff count"
                        onClick={() => setPositions((isValid ? positions : 1) + 1)}
                        disabled={isValid && positions >= 50}
                        className="h-12 w-12 rounded-xl text-lg active:scale-95"
                      >+</Button>
                    </div>
                    {errorMsg && (
                      <p id="positions-error" role="alert" className="text-xs text-destructive">
                        {errorMsg}
                      </p>
                    )}
                    {isValid && (
                      <div className="flex items-center justify-between rounded-xl bg-accent/8 px-3 py-2.5 text-xs">
                        <span className="text-muted-foreground">
                          Platform fee
                        </span>
                        <span className="text-foreground font-semibold">£{feePer.toFixed(2)} per worker</span>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">How many bartenders to hire. The listing closes automatically once this many accept. A flat £{feePer.toFixed(2)} platform fee applies per booked worker.</p>
                  </div>
                );
              })()}
            </div>
            <div className="flex items-center justify-between rounded-xl bg-muted/40 px-4 py-3">
              <div>
                <Label htmlFor="dbs" className="cursor-pointer">Requires DBS</Label>
                <p className="text-xs text-muted-foreground mt-1">Restrict to bartenders with current DBS clearance.</p>
              </div>
              <Switch id="dbs" checked={form.requires_dbs} onCheckedChange={(v) => setForm({ ...form, requires_dbs: v })} />
            </div>
              </>
            )}

            {step === 3 && (
              <>
            <div>
              <Label htmlFor="address">Venue Address <span className="text-destructive">*</span></Label>
              <Input id="address" required value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="12 Berkeley Square, Mayfair" className="mt-2 h-12 rounded-xl text-base" />
            </div>
            <div>
              <Label htmlFor="postcode">Postcode <span className="text-destructive">*</span></Label>
              <Input id="postcode" required value={form.postcode}
                onChange={(e) => setForm({ ...form, postcode: e.target.value.toUpperCase() })}
                placeholder="W1J 6BR" className="mt-2 h-12 rounded-xl text-base" />
              <p className="mt-2 text-xs text-muted-foreground">Used to compute distance for nearby bartenders.</p>
            </div>
            <div className="rounded-2xl bg-muted/40 p-4 space-y-4">
              <div>
                <p className="tracking-wide-luxury text-[10px] uppercase text-accent">On-site contact</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  The manager or owner responsible for this shift. Shared with the booked bartender only.
                </p>
              </div>
              <div>
                <Label htmlFor="contact_name">Full name <span className="text-destructive">*</span></Label>
                <Input id="contact_name" required value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  placeholder="Alex Morgan" className="mt-2 h-12 rounded-xl text-base" />
              </div>
              <div>
                <Label htmlFor="contact_phone">Direct phone number <span className="text-destructive">*</span></Label>
                <Input id="contact_phone" required type="tel" inputMode="tel" value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  placeholder="07700 900123" className="mt-2 h-12 rounded-xl text-base" />
              </div>
              <div>
                <Label htmlFor="contact_job_title">Job title <span className="text-destructive">*</span></Label>
                <Input id="contact_job_title" required value={form.contact_job_title}
                  onChange={(e) => setForm({ ...form, contact_job_title: e.target.value })}
                  placeholder="General Manager" className="mt-2 h-12 rounded-xl text-base" />
              </div>
            </div>
            <div>
              <Label htmlFor="early">Favorites Early Access (hours) <span className="text-destructive">*</span></Label>
              <Input id="early" required type="number" min="0" max="168" step="1" value={form.favorites_early_access_hours}
                onChange={(e) => setForm({ ...form, favorites_early_access_hours: e.target.value })} className="mt-2 h-12 rounded-xl text-base" />
              <p className="mt-2 text-xs text-muted-foreground">If &gt; 0, only your favorited bartenders can see and apply to this engagement for that many hours after posting. Set to 0 to open to all members immediately.</p>
            </div>
            <div>
              <Label htmlFor="notes">Brief (optional)</Label>
              <Textarea id="notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-xl" />
              <p className="mt-2 text-xs text-muted-foreground">Jot down the vibe, dress code and service style — keep it short and clear.</p>
            </div>

              </>
            )}

            {step === 4 && (
              <>
            {(() => {
              const s = form.start_time ? new Date(form.start_time).getTime() : NaN;
              const e = form.end_time ? new Date(form.end_time).getTime() : NaN;
              const hrs = isFinite(s) && isFinite(e) && e > s ? (e - s) / 3_600_000 : 0;
              if (hrs <= 6) return null;
              const blocked = !form.break_acknowledged;
              return (
                <div className="rounded-2xl bg-muted/40 p-4 space-y-3">
                  <div>
                    <p className="tracking-wide-luxury text-[10px] uppercase text-accent">UK Working Time Regulations</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Shifts over 6 hours legally require a 20-minute uninterrupted rest break.
                    </p>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={form.break_acknowledged}
                      onCheckedChange={(v) => setForm({ ...form, break_acknowledged: v === true })}
                      className="mt-0.5"
                    />
                    <span className="text-sm leading-snug">
                      I confirm the worker will be provided a 20-minute uninterrupted rest break.
                      <span className="text-destructive"> *</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={!form.break_is_paid}
                      onCheckedChange={(v) => setForm({ ...form, break_is_paid: v !== true })}
                      className="mt-0.5"
                    />
                    <span className="text-sm leading-snug">
                      Break is <strong>unpaid</strong> (20 minutes will be deducted from billable hours).
                      <span className="block text-xs text-muted-foreground mt-0.5">Default: paid. Most UK hospitality contracts pay through the break.</span>
                    </span>
                  </label>
                  {blocked && (
                    <p className="text-xs text-destructive">You must acknowledge the rest break before posting.</p>
                  )}
                </div>
              );
            })()}
            {(() => {
              if (!form.start_time || !form.end_time) return null;
              if (!(["bartender", "bar_back", "mixologist"] as const).includes(form.role_type as ShiftRole)) return null;
              const role = PAY_RATES[form.role_type as ShiftRole];
              const minRate = role.venue_pence;
              const rate = form.hourly_rate.trim() ? Math.max(minRate, Math.round(Number(form.hourly_rate) * 100) || minRate) : minRate;
              const positions = Math.max(1, Math.min(50, Math.floor(Number(form.positions_needed) || 1)));
              const startIso = (() => { try { return new Date(form.start_time).toISOString(); } catch { return ""; } })();
              const endIso = (() => { try { return new Date(form.end_time).toISOString(); } catch { return ""; } })();
              if (!startIso || !endIso) return null;
              return (
                <FillConfidenceMeter
                  role_type={form.role_type as "bartender" | "bar_back" | "mixologist"}
                  hourly_rate_pence={rate}
                  start_time={startIso}
                  end_time={endIso}
                  positions={positions}
                  requires_dbs={form.requires_dbs}
                  onApplyRecommendedRate={(pence) =>
                    setForm((f) => ({ ...f, hourly_rate: (pence / 100).toFixed(2) }))
                  }
                />
              );
            })()}
            {(() => {

              const s = form.start_time ? new Date(form.start_time).getTime() : NaN;
              const e = form.end_time ? new Date(form.end_time).getTime() : NaN;
              const rawHrs = isFinite(s) && isFinite(e) && e > s ? (e - s) / 3_600_000 : 0;
              const billable = rawHrs > 6 && !form.break_is_paid ? Math.max(0, rawHrs - 20 / 60) : rawHrs;
              const role = (["bartender", "bar_back", "mixologist"] as const).includes(form.role_type as ShiftRole)
                ? PAY_RATES[form.role_type as ShiftRole]
                : PAY_RATES.bartender;
              const minRate = role.venue_pence / 100;
              const rate = form.hourly_rate.trim() ? Math.max(minRate, Number(form.hourly_rate) || minRate) : minRate;
              const positions = Math.max(1, Math.min(50, Math.floor(Number(form.positions_needed) || 1)));
              const feePer = tieredPlatformFeePence(positions) / 100;
              const wage = rate * billable * positions;
              const feesTotal = feePer * positions;
              const total = wage + feesTotal;
              const ready = billable > 0;
              const unitsBefore = creditUnits(creditPence);
              const creditApplied = ready ? creditAppliedPence(creditPence, Math.round(total * 100)) / 100 : 0;
              const unitsAfter = Math.max(0, unitsBefore - (creditApplied > 0 ? 1 : 0));
              const totalAfterCredit = Math.max(0, total - creditApplied);



              return (
                <div className="rounded-2xl bg-foreground p-5 text-background shadow-[0_16px_40px_-24px_rgba(0,0,0,0.7)] space-y-2">
                  <p className="tracking-wide-luxury text-[10px] uppercase opacity-70">
                    {form.is_asap ? "ASAP — pay starts at arrival" : "Your total cost"}
                  </p>
                  {ready ? (
                    <>
                      <div className="flex items-baseline justify-between">
                        <span className="text-sm opacity-80">{form.is_asap ? "Max" : "Total"}</span>
                        <span className="font-display text-3xl tabular-nums">£{totalAfterCredit.toFixed(2)}</span>
                      </div>
                      {form.is_asap && (
                        <p className="text-[11px] opacity-70">
                          You're only charged for the time between both-party arrival confirmation and the shift end — capped at the {billable.toFixed(billable % 1 ? 1 : 0)}h window shown.
                        </p>
                      )}

                      <ul className="space-y-1 border-t border-background/20 pt-2 text-xs opacity-80">
                        <li className="flex justify-between gap-3">
                          <span>Bartender wages · £{rate.toFixed(2)}/hr × {billable.toFixed(billable % 1 ? 2 : 0)}h × {positions}</span>
                          <span className="tabular-nums">£{wage.toFixed(2)}</span>
                        </li>
                        <li className="flex justify-between gap-3">
                          <span>Platform fee · £{feePer.toFixed(2)} × {positions}</span>
                          <span className="tabular-nums">£{feesTotal.toFixed(2)}</span>
                        </li>
                        {creditApplied > 0 && (
                          <li className="flex justify-between gap-3">
                            <span>Booking credit applied · 1 of {unitsBefore}</span>
                            <span className="tabular-nums">− £{creditApplied.toFixed(2)}</span>
                          </li>
                        )}
                      </ul>
                      {creditApplied > 0 && (
                        <p className="text-[11px] opacity-70">
                          {unitsAfter} credit{unitsAfter === 1 ? "" : "s"} will remain after this booking.
                        </p>
                      )}


                    </>
                  ) : (
                    <p className="text-xs opacity-70">Set a start and end time to see your full cost.</p>
                  )}
                </div>
              );
            })()}
            {waiversRemaining > 0 && (() => {
              const tooBig = Math.max(1, Math.min(50, Math.floor(Number(form.positions_needed) || 1))) > 5;
              return (
                <label className="flex cursor-pointer items-start gap-3 rounded-2xl bg-accent/8 p-4 text-xs">
                  <input
                    type="checkbox"
                    checked={form.use_referral_waiver && !tooBig}
                    disabled={tooBig}
                    onChange={(e) => setForm((f) => ({ ...f, use_referral_waiver: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 accent-accent"
                  />
                  <span className="text-foreground">
                    {tooBig
                      ? <>Fee waivers apply to shifts of up to 5 workers. <strong>{waiversRemaining}</strong> credits saved for next time.</>
                      : <>Auto-applying your earned fee waiver — <strong>{waiversRemaining}</strong> credit{waiversRemaining === 1 ? "" : "s"} left.
                        Uncheck to save it for a future booking.</>}
                  </span>
                </label>
              );
            })()}
              </>
            )}
            </div>

            {/* Wizard controls */}
            <div className="mt-6 flex items-center gap-3">
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep((v) => Math.max(1, v - 1))}
                  className="h-12 rounded-xl px-5 tracking-wide-luxury text-xs active:scale-[0.98]"
                >
                  Back
                </Button>
              )}
              {step < 4 ? (
                <Button
                  type="button"
                  disabled={!stepReady}
                  onClick={() => setStep((v) => Math.min(4, v + 1))}
                  className="h-12 flex-1 rounded-xl bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury text-sm shadow-[0_10px_24px_-14px_rgba(0,0,0,0.8)] transition-transform active:scale-[0.98]"
                >
                  Continue
                </Button>
              ) : (
                <Button type="submit" disabled={posting || lateFees.length > 0 || !posValid || breakBlocked} className="h-12 flex-1 rounded-xl bg-accent text-primary-foreground hover:bg-accent/90 tracking-wide-luxury text-sm shadow-[0_10px_24px_-14px_rgba(0,0,0,0.8)] transition-transform active:scale-[0.98]">
                  {breakBlocked ? "Confirm rest break to post" : lateFees.length > 0 ? "Settle late fee to post" : !posValid ? "Fix staff count to post" : posting ? "Posting…" : "Post to Register"}
                </Button>
              )}
            </div>

          </form>
            );
          })()}

          <div className="mt-6"><StrikesPanel /></div>
        </section>

        <section>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="tracking-wide-luxury text-accent">Your Register</p>
              <h2 className="mt-2 font-display text-3xl tracking-tight sm:text-4xl">Active Engagements</h2>
              <span className="gold-divider mt-4" />
            </div>
            <div className="flex gap-2 mt-1 flex-wrap items-center">
              <ExportReportButton scope="mine" />
              <Link
                to="/venue/previous"
                className="inline-block rounded-xl border border-border/70 bg-card px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground shadow-[0_4px_14px_-10px_rgba(0,0,0,0.5)] transition-colors hover:border-foreground hover:text-foreground"
              >
                Previous Engagements →
              </Link>
              <Link
                to="/venue-kpis"
                className="inline-block rounded-xl border border-border/70 bg-card px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground shadow-[0_4px_14px_-10px_rgba(0,0,0,0.5)] transition-colors hover:border-foreground hover:text-foreground"
              >
                KPIs →
              </Link>
              <Link
                to="/favorites"
                className="inline-block rounded-xl border border-border/70 bg-card px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground shadow-[0_4px_14px_-10px_rgba(0,0,0,0.5)] transition-colors hover:border-foreground hover:text-foreground"
              >
                Favorite Bartenders →
              </Link>
            </div>
          </div>
          <div className="mt-8 space-y-4">
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {(() => {
              // Active engagements stay on the home page until:
              //  - the shift is completed/cancelled, OR
              //  - both venue and bartender have confirmed completion, OR
              //  - the shift ran unfilled (no bartender assigned) and its end time has passed.
              const now = Date.now();
              const current = shifts.filter((s) => {
                if (isHiddenFromDashboard(s)) return false;
                if (s.status === "completed" || s.status === "cancelled") return false;
                if (s.venue_confirmed_at && s.bartender_confirmed_at) return false;
                const end = s.end_time ? new Date(s.end_time).getTime() : NaN;
                if (Number.isFinite(end) && end <= now && !s.bartender_id) return false;
                return true;
              });
              if (!loading && current.length === 0) {
                return (
                  <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                    No current engagements. <Link to="/venue/previous" className="underline hover:text-foreground">View previous engagements →</Link>
                  </div>
                );
              }
              return current.map((s) => (
                <ShiftRow
                  key={s.id}
                  shift={s}
                  bartender={s.bartender_id ? bartenders[s.bartender_id] : undefined}
                  applicants={applications.filter((a) => a.shift_id === s.id)}
                  bartenderMap={bartenders}
                  bartenderStats={bartenderStats}
                  favorites={favorites}
                  onToggleFavorite={handleToggleFavorite}
                  favToggling={favTogglingId}
                  reviewed={reviewedIds.has(s.id)}
                  onReviewed={load}
                  autoReview={autoReviewId === s.id}
                  onPay={() => setPayingShiftId(s.id)}
                  onConfirm={() => handleConfirm(s.id)}
                  onCancel={() => handleCancel(s.id)}
                  onDismiss={() => handleDismiss(s.id)}
                  acting={actingId === s.id}
                  onDecide={async (bookingId, decision) => {
                    setDecidingId(bookingId);
                    try {
                      await decide({ data: { booking_id: bookingId, decision } });
                      toast.success(decision === "accepted" ? "Bartender confirmed — proceed to payment." : "Application declined.");
                      await load();
                    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not update application."); }
                    finally { setDecidingId(null); }
                  }}
                  deciding={decidingId}
                />
              ));
            })()}
          </div>
        </section>
      </main>


      <Dialog open={!!payingShiftId} onOpenChange={(o) => { if (!o) { setPayingShiftId(null); load(); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0">
          <DialogHeader className="sticky top-0 z-10 bg-background border-b border-border px-6 py-4 flex flex-row items-center justify-between gap-4 sm:text-left">
            <DialogTitle>Pay to confirm engagement</DialogTitle>
            <button
              onClick={() => { setPayingShiftId(null); load(); }}
              className="text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground border border-border px-3 py-1.5 hover:border-foreground transition-colors shrink-0"
            >
              ← Back to Dashboard
            </button>
          </DialogHeader>
          <div className="px-6 pb-6">
            {payingShiftId && <ShiftCheckout shiftId={payingShiftId} />}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payingFeeId} onOpenChange={(o) => { if (!o) { setPayingFeeId(null); load(); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0">
          <DialogHeader className="sticky top-0 z-10 bg-background border-b border-border px-6 py-4 flex flex-row items-center justify-between gap-4 sm:text-left">
            <DialogTitle>Pay late cancellation fee</DialogTitle>
            <button
              onClick={() => { setPayingFeeId(null); load(); }}
              className="text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground border border-border px-3 py-1.5 hover:border-foreground transition-colors shrink-0"
            >
              ← Back to Dashboard
            </button>
          </DialogHeader>
          <div className="px-6 pb-6">
            {payingFeeId && <LateFeeCheckout feeId={payingFeeId} />}
          </div>
        </DialogContent>
      </Dialog>

      <CancelShiftDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onConfirm={doCancel}
        busy={!!actingId}
        showPenaltyWarning={cancelHasConfirmedStaff}
        policy={cancelPolicy}
        policyText={
          cancelHasConfirmedStaff
            ? "Free to cancel more than 24h before the start time. Within 24h, confirmed staff keep 50% of their wage and the platform fee is retained; the rest is refunded."
            : "Nothing is owed — no bartender is confirmed for this listing."
        }
      />
    </div>
  );
}

function ShiftRow({
  shift, bartender, applicants, bartenderMap, bartenderStats, favorites, onToggleFavorite, favToggling, reviewed, onReviewed, autoReview,
  onDecide, deciding, onPay, onConfirm, onCancel, onDismiss, acting,
}: {
  shift: Shift;
  bartender?: BartenderLite;
  applicants: Application[];
  bartenderMap: Record<string, BartenderLite>;
  bartenderStats: Record<string, BartenderStats>;
  favorites: Set<string>;
  onToggleFavorite: (bartenderId: string) => void | Promise<void>;
  favToggling: string | null;
  reviewed: boolean;
  onReviewed: () => void;
  autoReview?: boolean;
  onDecide: (bookingId: string, decision: "accepted" | "denied") => void | Promise<void>;
  deciding: string | null;
  onPay: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  onDismiss: () => void;
  acting: boolean;
}) {
  const start = new Date(shift.start_time);
  const end = new Date(shift.end_time);
  const hours = (end.getTime() - start.getTime()) / 3_600_000;
  const positions = Math.max(1, shift.positions_needed ?? 1);
  const totalPence = Math.round(Number(shift.hourly_rate) * hours * positions * 100);
  const acceptedCount = applicants.filter((a) => a.status === "accepted").length;

  const labels: Record<string, { text: string; cls: string; dot: string }> = {
    open:             { text: "Open",             cls: "bg-accent/10 text-accent border-accent/40",                 dot: "bg-accent" },
    awaiting_payment: { text: "Awaiting Payment", cls: "bg-amber-500/10 text-amber-700 border-amber-500/40",        dot: "bg-amber-500" },
    paid:             { text: "Paid",              cls: "bg-emerald-500/10 text-emerald-700 border-emerald-500/40", dot: "bg-emerald-500" },
    filled:           { text: "Filled",            cls: "bg-foreground/10 text-foreground border-foreground/30",    dot: "bg-foreground" },
    completed:        { text: "Completed",         cls: "bg-muted text-muted-foreground border-border",              dot: "bg-muted-foreground" },
    cancelled:        { text: "Cancelled",         cls: "bg-muted text-muted-foreground border-border",              dot: "bg-muted-foreground" },
    disputed:         { text: "Disputed",          cls: "bg-red-500/10 text-red-700 border-red-500/40",              dot: "bg-red-500" },
  };
  const s = labels[shift.status] ?? labels.open;
  const [showReview, setShowReview] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingTimes, setEditingTimes] = useState(false);
  const [savingTimes, setSavingTimes] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [reSaving, setReSaving] = useState(false);
  const [reReason, setReReason] = useState("");
  const review = useServerFn(submitReview);
  const updateTimes = useServerFn(updateShiftTimes);
  const doReschedule = useServerFn(rescheduleShift);
  const canReview = shift.status === "completed" && !reviewed && shift.bartender_id;
  const canConfirm = shift.status === "paid" && end.getTime() <= Date.now() && !shift.venue_confirmed_at;
  const canCancel = ["open", "awaiting_payment", "paid"].includes(shift.status) && start.getTime() > Date.now();
  // Pay becomes available as soon as at least one bartender is confirmed —
  // not only when every position is filled.
  const canPay = shift.payment_status === "unpaid"
    && (shift.status === "awaiting_payment" || (shift.status === "open" && acceptedCount > 0));
  const canEditTimes = shift.status === "open" && acceptedCount === 0 && start.getTime() > Date.now();
  const canReschedule = ["awaiting_payment", "paid"].includes(shift.status) || (shift.status === "open" && acceptedCount > 0);
  const noShowOpensAt = noShowOpensAtMs(shift.start_time, shift.end_time);
  const canReportNoShow = shift.status === "paid" && Date.now() >= noShowOpensAt && Date.now() <= noShowClosesAtMs(shift.end_time);

  const [compareOpen, setCompareOpen] = useState(false);
  const [misconductOpen, setMisconductOpen] = useState(false);
  const [confirmTerminateOpen, setConfirmTerminateOpen] = useState(false);

  const [mutualOpen, setMutualOpen] = useState(false);
  // Emergency stop: only while the shift is active (live window, not completed).
  const isActiveBooking = !!shift.bartender_id
    && (["paid", "awaiting_payment", "filled"] as string[]).includes(shift.status as string);
  const canEmergencyStop = isActiveBooking
    && start.getTime() - Date.now() <= 6 * 3_600_000
    && end.getTime() >= Date.now();
  const canMutualFinish = isActiveBooking && Date.now() >= start.getTime() && Date.now() <= end.getTime();

  useEffect(() => {
    if (autoReview && canReview) {
      setShowReview(true);
      const el = document.getElementById(`shift-${shift.id}`);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoReview, canReview]);

  const pad = (n: number) => String(n).padStart(2, "0");
  const toLocalInput = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [editStart, setEditStart] = useState(() => toLocalInput(shift.start_time));
  const [editEnd, setEditEnd] = useState(() => toLocalInput(shift.end_time));




  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await review({ data: { shift_id: shift.id, rating, comment: comment || null } });
      toast.success("Review filed.");
      onReviewed();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not file review."); }
    finally { setSubmitting(false); }
  };

  const handleSaveTimes = async () => {
    setSavingTimes(true);
    try {
      await updateTimes({
        data: {
          shift_id: shift.id,
          start_time: new Date(editStart).toISOString(),
          end_time: new Date(editEnd).toISOString(),
        },
      });
      toast.success("Times updated.");
      setEditingTimes(false);
      onReviewed();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not update times."); }
    finally { setSavingTimes(false); }
  };

  const closeListing = useServerFn(setShiftListingClosed);
  const [closingListing, setClosingListing] = useState(false);

  const pending = applicants.filter((a) => a.status === "pending");
  const denied = applicants.filter((a) => a.status === "denied");

  const dateLabel = start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  const timeLabel = `${start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} – ${end.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;

  return (
    <article id={`shift-${shift.id}`} className="group overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[0_8px_30px_-22px_rgba(0,0,0,0.45)] transition-all hover:border-foreground/25 hover:shadow-[0_14px_40px_-24px_rgba(0,0,0,0.55)]">
      {/* Compact header strip */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border/60 bg-muted/30 px-5 py-3">
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[0.65rem] tracking-wide-luxury uppercase ${s.cls}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
          {s.text}
        </span>
        <span className="text-[0.7rem] tracking-wide-luxury text-muted-foreground tabular-nums">
          {acceptedCount}/{positions} staffed
        </span>
        {shift.status === "open" && shift.listing_closed_at && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[0.6rem] uppercase tracking-wide-luxury text-muted-foreground">Listing closed</span>
        )}
        {shift.requires_dbs && (
          <span className="text-[0.6rem] tracking-wide-luxury uppercase text-accent border border-accent/40 rounded-sm px-1.5 py-0.5">DBS</span>
        )}
        <span className="ml-auto font-display text-sm tabular-nums">£{(totalPence / 100).toFixed(2)}</span>
        {acceptedCount > 0 && (
          <Link to="/shift/$id/notes" params={{ id: shift.id }}
            className="text-[0.65rem] tracking-wide-luxury uppercase border border-border px-2 py-0.5 hover:border-accent">
            Notes
          </Link>
        )}
        {(["open", "awaiting_payment", "paid", "completed"] as ShiftStatus[]).includes(shift.status) && (
          <SaveAsTemplateButton shift={shift} positionsNeeded={positions} />
        )}
        {(end.getTime() <= Date.now() || ["completed", "cancelled"].includes(shift.status)) && (
          <button
            type="button"
            onClick={onDismiss}
            title="Remove from dashboard"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-background text-muted-foreground text-lg leading-none transition-colors hover:border-destructive hover:bg-destructive hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <span aria-hidden>×</span>
            <span className="sr-only">Remove this job from the dashboard</span>
          </button>
        )}

      </div>

      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-3 flex-wrap">
              <p className="font-display text-xl leading-none">{dateLabel}</p>
              <p className="text-sm tabular-nums text-muted-foreground">{timeLabel}</p>
              {canEditTimes && !editingTimes && (
                <button
                  type="button"
                  onClick={() => setEditingTimes(true)}
                  className="text-[0.65rem] tracking-wide-luxury uppercase text-muted-foreground hover:text-accent underline-offset-4 hover:underline"
                >
                  Edit times
                </button>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {PAY_RATES[(["bartender","bar_back","mixologist"] as const).includes(shift.role_type as ShiftRole) ? (shift.role_type as ShiftRole) : "bartender"].label} · £{Number(shift.hourly_rate).toFixed(2)}/hr · {positions} staff · {hours.toFixed(hours % 1 ? 1 : 0)}h
            </p>
            {(shift.address || shift.postcode) && (
              <p className="mt-1 text-xs text-muted-foreground">
                {[shift.address, shift.postcode].filter(Boolean).join(" · ")}
              </p>
            )}
            {shift.status === "open" && shift.favorites_only_until && new Date(shift.favorites_only_until).getTime() > Date.now() && (
              <p className="mt-2 tracking-wide-luxury text-[0.65rem] text-accent">
                ★ Favorites-only until {new Date(shift.favorites_only_until).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
            {shift.notes && <p className="mt-3 text-sm text-foreground/80">{shift.notes}</p>}
            {bartender && (
              <p className="mt-3 text-xs tracking-wide-luxury text-muted-foreground">
                Confirmed · {bartender.full_name || "Unnamed"}{bartender.is_verified ? " · Verified" : ""}
                {shift.bartender_id && (
                  <button
                    type="button"
                    onClick={() => onToggleFavorite(shift.bartender_id!)}
                    disabled={favToggling === shift.bartender_id}
                    className={`ml-3 align-middle ${favorites.has(shift.bartender_id) ? "text-accent" : "text-muted-foreground hover:text-accent"}`}
                    title={favorites.has(shift.bartender_id) ? "Unfavorite" : "Add to favorites"}
                  >
                    {favorites.has(shift.bartender_id) ? "★ Favorited" : "☆ Favorite"}
                  </button>
                )}
                {shift.bartender_id && (
                  <Link
                    to="/bartenders/$id"
                    params={{ id: shift.bartender_id }}
                    className="ml-3 align-middle text-accent hover:underline"
                  >
                    View portfolio →
                  </Link>
                )}
                {shift.bartender_id && ["paid", "completed"].includes(shift.status) && (
                  <CallBartenderButton bartenderId={shift.bartender_id} />
                )}
              </p>
            )}
            {shift.status === "paid" && shift.venue_confirmed_at && !shift.bartender_confirmed_at && (
              <p className="mt-3 text-[0.7rem] tracking-wide-luxury text-amber-700">You confirmed. Awaiting bartender.</p>
            )}
            {shift.status === "paid" && start.getTime() - Date.now() <= 2 * 3_600_000 && end.getTime() >= Date.now() && (
              <ArrivalPanel
                bookings={applicants.filter((a) => a.status === "accepted")}
                bartenderMap={bartenderMap}
                onConfirmed={onReviewed}
                shiftStartIso={shift.start_time}
              />
            )}

            {shift.status === "completed" && (
              <TipAllocationSection
                shift={shift}
                bartenderName={bartender?.full_name ?? null}
                onAllocated={onReviewed}
              />
            )}
          </div>
        </div>

        {editingTimes && (
          <div className="mt-4 border border-accent/40 bg-background p-4 space-y-3">
            <ShiftTimePicker
              startValue={editStart}
              endValue={editEnd}
              onChange={(v) => { setEditStart(v.start_time); setEditEnd(v.end_time); }}
            />
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setEditingTimes(false); setEditStart(toLocalInput(shift.start_time)); setEditEnd(toLocalInput(shift.end_time)); }}
                disabled={savingTimes}
                className="rounded-none border-border tracking-wide-luxury text-xs"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSaveTimes}
                disabled={savingTimes || !editStart || !editEnd}
                className="rounded-none bg-accent text-primary-foreground hover:bg-accent/90 tracking-wide-luxury text-xs"
              >
                {savingTimes ? "Saving…" : "Save times"}
              </Button>
            </div>
          </div>
        )}
      </div>


      {shift.status === "open" && (
        <div className="mt-5 border-t border-border px-5 pt-5 pb-1">
          <div className="flex items-center justify-between gap-2">
            <p className="tracking-wide-luxury text-[0.65rem] text-accent">
              Applicants {pending.length > 0 && `· ${pending.length} awaiting`}
            </p>
            {pending.length >= 2 && (
              <button
                type="button"
                onClick={() => setCompareOpen(true)}
                className="text-[0.65rem] tracking-wide-luxury uppercase border border-border px-2 py-0.5 hover:border-accent"
              >
                Compare side-by-side
              </button>
            )}
          </div>
          {applicants.length === 0 ? (
            <p className="mt-4 pr-2 pb-4 text-sm leading-relaxed text-muted-foreground">No interest registered yet.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {applicants.map((a) => {
                const b = bartenderMap[a.bartender_id];
                const stats = bartenderStats[a.bartender_id];
                return (
                  <li key={a.id} className="border border-border bg-background p-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex items-start gap-3">
                        <ApplicantAvatar path={b?.avatar_path ?? null} name={b?.full_name ?? null} size={48} />
                        <div className="min-w-0">
                          <Link
                            to="/bartenders/$id"
                            params={{ id: a.bartender_id }}
                            className="font-display text-base hover:text-accent hover:underline"
                          >
                            {b?.full_name || "Member"}
                          </Link>
                          <p className="mt-1 text-[0.65rem] tracking-wide-luxury text-muted-foreground">
                            {b?.is_verified ? "Verified · " : ""}{b?.has_dbs ? "DBS · " : ""}
                            {a.status === "pending" ? "Pending" : a.status === "denied" ? "Declined" : "Confirmed"}
                          </p>
                          {stats && (
                            <div className="mt-2 flex items-center gap-3">
                              {typeof stats.avg_rating === "number" && (
                                <span className="flex items-center gap-1 text-[0.65rem] tracking-wide-luxury text-accent">
                                  <span>{"★".repeat(Math.round(stats.avg_rating))}</span>
                                  <span>{stats.avg_rating.toFixed(1)}</span>
                                </span>
                              )}
                              <span className="text-[0.65rem] tracking-wide-luxury text-muted-foreground">
                                {stats.completed_count} completed
                              </span>
                            </div>
                          )}
                          {((b?.skills && b.skills.length > 0) ||
                            (typeof b?.total_months_experience === "number" &&
                              b.total_months_experience > 0)) && (
                            <div className="mt-2">
                              <p className="text-[0.6rem] tracking-wide-luxury uppercase text-muted-foreground">Skills</p>
                              {typeof b?.total_months_experience === "number" &&
                                b.total_months_experience > 0 && (
                                  <p className="mt-1 text-[0.7rem] tracking-wide-luxury text-foreground/80">
                                    {formatBarExperience(b.total_months_experience)} behind the bar
                                  </p>
                                )}
                              {b?.skills && b.skills.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {b.skills.slice(0, 8).map((s) => (
                                    <span
                                      key={s}
                                      className="border border-border px-2 py-0.5 text-[0.65rem] tracking-wide-luxury text-foreground/80"
                                    >
                                      {s}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {b?.about_me && <p className="mt-2 text-xs text-foreground/80 whitespace-pre-wrap">{b.about_me}</p>}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => onToggleFavorite(a.bartender_id)}
                          disabled={favToggling === a.bartender_id}
                          className={`rounded-none border-border tracking-wide-luxury text-xs ${favorites.has(a.bartender_id) ? "text-accent border-accent" : ""}`}
                          title={favorites.has(a.bartender_id) ? "Unfavorite" : "Add to favorites"}
                        >
                          {favorites.has(a.bartender_id) ? "★ Favorited" : "☆ Favorite"}
                        </Button>
                        <ViewCvButton shiftId={a.shift_id} bartenderId={a.bartender_id} name={b?.full_name ?? null} />
                        <ApplicantChatButton shiftId={a.shift_id} applicantName={b?.full_name ?? null} />
                        {a.status === "pending" && (
                          <>
                            <Button type="button" disabled={deciding === a.id} onClick={() => onDecide(a.id, "accepted")} className="rounded-none bg-accent text-primary-foreground hover:bg-accent/90 tracking-wide-luxury text-xs">
                              {deciding === a.id ? "…" : "Accept"}
                            </Button>
                            <Button type="button" variant="outline" disabled={deciding === a.id} onClick={() => onDecide(a.id, "denied")} className="rounded-none border-border tracking-wide-luxury text-xs">Deny</Button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {(canPay || canConfirm || canCancel || (bartender && shift.status !== "open")) && (
        <div className="mt-4 flex flex-wrap gap-3 border-t border-border px-5 pt-5 pb-5">
          {canPay && (
            <Button onClick={onPay} className="rounded-none bg-accent text-primary-foreground hover:bg-accent/90 tracking-wide-luxury text-xs">
              Pay £{(totalPence / 100).toFixed(2)} to confirm
            </Button>
          )}
          {canConfirm && (
            <Button onClick={onConfirm} disabled={acting} className="rounded-none bg-accent text-primary-foreground hover:bg-accent/90 tracking-wide-luxury text-xs">
              {acting ? "…" : "Confirm Completion"}
            </Button>
          )}
          {bartender && (
            <ViewCvButton shiftId={shift.id} bartenderId={bartender.id} name={bartender.full_name} />
          )}
          {canReschedule && start.getTime() > Date.now() && (
            <Button type="button" variant="outline" onClick={() => { setReReason(""); setRescheduleOpen(true); }} className="rounded-none border-border tracking-wide-luxury text-xs">
              Reschedule
            </Button>
          )}
          {shift.status === "open" && (
            <Button
              type="button"
              variant="outline"
              disabled={closingListing}
              onClick={async () => {
                setClosingListing(true);
                try {
                  await closeListing({ data: { shift_id: shift.id, closed: !shift.listing_closed_at } });
                  toast.success(shift.listing_closed_at ? "Listing reopened." : "Listing closed to new applicants.");
                  onReviewed();
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : "Could not update listing.");
                } finally {
                  setClosingListing(false);
                }
              }}
              className="rounded-xl border-border tracking-wide-luxury text-xs"
            >
              {closingListing ? "…" : shift.listing_closed_at ? "Reopen listing" : "Close listing"}
            </Button>
          )}
          {canCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={acting} className="rounded-xl border-border tracking-wide-luxury text-xs">
              Cancel
            </Button>
          )}
          {canReportNoShow && (
            <Link to="/shift/$id/no-show" params={{ id: shift.id }}
              className="border border-destructive text-destructive px-3 py-2 tracking-wide-luxury text-xs hover:bg-destructive/5">
              Report No-Show
            </Link>
          )}
          {canMutualFinish && (
            <Button type="button" variant="outline" onClick={() => setMutualOpen(true)}
              className="rounded-none border-accent text-accent hover:bg-accent hover:text-accent-foreground tracking-wide-luxury text-xs">
              🤝 Mutual Finish
            </Button>
          )}
          {canEmergencyStop && (
            <Button type="button" variant="outline" onClick={() => setConfirmTerminateOpen(true)}
              className="rounded-none border-destructive bg-destructive/5 text-destructive hover:bg-destructive hover:text-background tracking-wide-luxury text-xs">
              ⛔ Terminate — Gross Misconduct
            </Button>
          )}

          {canReview && (
            <Button type="button" onClick={() => setShowReview((v) => !v)} className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury text-xs">
              {showReview ? "Close" : "File Review"}
            </Button>
          )}
          {reviewed && <span className="text-xs tracking-wide-luxury text-muted-foreground self-center">Review filed</span>}
          {denied.length > 0 && <span className="text-[0.65rem] tracking-wide-luxury text-muted-foreground self-center">· {denied.length} declined</span>}
        </div>
      )}

      {showReview && canReview && (
        <div className="mt-4 border border-border bg-background p-4 space-y-3">
          <div>
            <Label className="text-xs tracking-wide-luxury">Rating</Label>
            <div className="mt-2 flex gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} className={`text-xl ${n <= rating ? "text-accent" : "text-muted-foreground"}`}>★</button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor={`c-${shift.id}`} className="text-xs tracking-wide-luxury">Note (optional)</Label>
            <Textarea id={`c-${shift.id}`} rows={3} value={comment} onChange={(e) => setComment(e.target.value)} className="mt-2" />
          </div>
          <Button onClick={handleSubmit} disabled={submitting} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury">
            {submitting ? "Filing…" : "File Review"}
          </Button>
        </div>
      )}

      <Dialog open={rescheduleOpen} onOpenChange={(o) => { if (!o) setRescheduleOpen(false); }}>
        <DialogContent className="rounded-none border-border bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Reschedule engagement</DialogTitle>
            <DialogDescription>
              Accepted bartenders will be notified. The change is recorded for admin review.
            </DialogDescription>
          </DialogHeader>
          <ShiftTimePicker
            startValue={editStart}
            endValue={editEnd}
            onChange={(v) => { setEditStart(v.start_time); setEditEnd(v.end_time); }}
          />
          <div className="mt-2">
            <Label htmlFor={`re-${shift.id}`} className="text-xs tracking-wide-luxury">Reason (required)</Label>
            <Textarea id={`re-${shift.id}`} rows={3} value={reReason}
              onChange={(e) => setReReason(e.target.value.slice(0, 500))}
              placeholder="e.g. Event organiser shifted start by an hour."
              className="mt-2 rounded-none" />
            <p className="text-right text-xs text-muted-foreground">{reReason.length}/500</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setRescheduleOpen(false)} disabled={reSaving}
              className="rounded-none tracking-wide-luxury">Cancel</Button>
            <Button type="button" disabled={reSaving || reReason.trim().length < 5}
              onClick={async () => {
                setReSaving(true);
                try {
                  await doReschedule({ data: {
                    shift_id: shift.id,
                    start_time: new Date(editStart).toISOString(),
                    end_time: new Date(editEnd).toISOString(),
                    reason: reReason.trim(),
                  }});
                  toast.success("Engagement rescheduled.");
                  setRescheduleOpen(false);
                  onReviewed();
                } catch (err) { toast.error(err instanceof Error ? err.message : "Could not reschedule."); }
                finally { setReSaving(false); }
              }}
              className="rounded-none bg-accent text-primary-foreground hover:bg-accent/90 tracking-wide-luxury">
              {reSaving ? "Saving…" : "Confirm reschedule"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmTerminateOpen} onOpenChange={setConfirmTerminateOpen}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Terminate this shift?</AlertDialogTitle>
            <AlertDialogDescription>
              This ends the engagement immediately, prorates pay to the minute of termination and
              freezes all funds while our team investigates. Only continue if gross misconduct has
              genuinely occurred.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none tracking-wide-luxury">Keep shift running</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmTerminateOpen(false); setMisconductOpen(true); }}
              className="rounded-none bg-destructive text-background hover:bg-destructive/90 tracking-wide-luxury"
            >
              Continue to termination
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <GrossMisconductDialog
        shiftId={shift.id}
        open={misconductOpen}
        onOpenChange={setMisconductOpen}
        onSubmitted={onReviewed}
      />

      <MutualFinishDialog
        shiftId={shift.id}
        shiftStart={shift.start_time}
        shiftEnd={shift.end_time}
        open={mutualOpen}
        onOpenChange={setMutualOpen}
        onSubmitted={onReviewed}
      />
      {isActiveBooking && <MutualFinishBanner shiftId={shift.id} onChanged={onReviewed} />}
      
      <ApplicantComparisonDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        applicants={pending}
        bartenderMap={bartenderMap}
        bartenderStats={bartenderStats}
        onDecide={onDecide}
        deciding={deciding}
      />
    </article>
  );
}

function RolePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const role = (["bartender", "bar_back", "mixologist"] as const).includes(value as ShiftRole)
    ? PAY_RATES[value as ShiftRole]
    : PAY_RATES.bartender;
  return (
    <div>
      <Label htmlFor="role">Role</Label>
      <select
        id="role"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full border border-border bg-background px-3 py-2 text-sm"
      >
        {Object.values(PAY_RATES).map((r) => (
          <option key={r.role} value={r.role}>
            {r.label} — £{(r.venue_pence / 100).toFixed(2)}/hr
          </option>
        ))}
      </select>
      <p className="mt-2 text-xs text-muted-foreground">
        You pay <strong>£{(role.venue_pence / 100).toFixed(2)}/hr</strong> straight to the bartender, plus a flat <strong>£5.00</strong> platform fee per booked bartender (concierge, vetting &amp; compliance).
      </p>

      <p className="mt-2 text-xs text-muted-foreground">
        Tips pass <strong>100%</strong> to the bartender under the UK Tipping Act 2023. Rates are fixed.
      </p>
    </div>
  );
}

function TipAllocationSection({
  shift,
  bartenderName,
  onAllocated,
}: {
  shift: Shift;
  bartenderName: string | null;
  onAllocated: () => void;
}) {
  const [open, setOpen] = useState(false);
  const status = shift.tip_payment_status ?? "pending";
  const tipPounds = ((shift.tip_amount_pence ?? 0) / 100).toFixed(2);

  return (
    <div className="mt-3 border border-border bg-muted/20 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[0.65rem] tracking-wide-luxury uppercase text-muted-foreground">Tip allocation (UK Tipping Act 2023)</span>
        {status === "pending" && (
          <span className="text-[0.65rem] tracking-wide-luxury uppercase text-amber-700">Action required</span>
        )}
        {status === "paid" && (
          <span className="text-[0.65rem] tracking-wide-luxury uppercase text-emerald-700">Paid · £{tipPounds}</span>
        )}
        {status === "not_applicable" && (
          <span className="text-[0.65rem] tracking-wide-luxury uppercase text-muted-foreground">No tips collected</span>
        )}
      </div>
      {status === "pending" && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-2 inline-flex text-[0.7rem] tracking-wide-luxury uppercase border border-accent text-accent px-3 py-1 hover:bg-accent hover:text-accent-foreground"
        >
          Allocate tips
        </button>
      )}
      {open && (
        <TipAllocationDialog
          shiftId={shift.id}
          bartenderName={bartenderName}
          onClose={() => setOpen(false)}
          onAllocated={onAllocated}
        />
      )}
    </div>
  );
}

function ArrivalPanel({
  bookings, bartenderMap, onConfirmed, shiftStartIso,
}: {
  bookings: Application[];
  bartenderMap: Record<string, BartenderLite>;
  onConfirmed: () => void;
  shiftStartIso: string;
}) {
  const confirmFn = useServerFn(confirmArrival);
  const [busyId, setBusyId] = useState<string | null>(null);
  if (bookings.length === 0) return null;

  const handle = async (bookingId: string) => {
    setBusyId(bookingId);
    try {
      const res = await confirmFn({ data: { booking_id: bookingId } });
      toast.success(res.bothConfirmed ? "Both sides confirmed — payment guaranteed." : "Arrival recorded. Awaiting bartender.");
      onConfirmed();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not confirm arrival."); }
    finally { setBusyId(null); }
  };

  const shiftStarted = new Date(shiftStartIso).getTime() <= Date.now();

  return (
    <div className="mt-4 border border-accent/40 bg-accent/5 p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="tracking-wide-luxury text-[0.65rem] uppercase text-accent">Arrival</p>
        {bookings[0] && <ShiftTimelineButton shiftId={bookings[0].shift_id} label="Timeline" />}
      </div>
      <ul className="mt-2 space-y-3">
        {bookings.map((b) => {
          const bartender = bartenderMap[b.bartender_id];
          const both = b.arrival_venue_confirmed_at && b.arrival_bartender_confirmed_at;
          return (
            <li key={b.id} className="space-y-2 border-b border-border/40 pb-2 last:border-b-0 last:pb-0">
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p className="font-medium truncate">{bartender?.full_name ?? "Bartender"}</p>
                  <p className="text-[0.7rem] text-muted-foreground">
                    {both
                      ? "✓ Payment guaranteed — both sides confirmed arrival."
                      : b.arrival_venue_confirmed_at
                        ? "You confirmed. Awaiting bartender arrival confirmation."
                        : b.arrival_bartender_confirmed_at
                          ? "Bartender confirmed. Tap to confirm they're here."
                          : "Tap when the bartender arrives on site."}
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() => handle(b.id)}
                  disabled={busyId === b.id || !!b.arrival_venue_confirmed_at}
                  className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury text-xs"
                >
                  {b.arrival_venue_confirmed_at ? "Confirmed" : busyId === b.id ? "…" : "Confirm Arrival"}
                </Button>
              </div>
              {shiftStarted && (
                <LateArrivalControl booking={b} shiftStartIso={shiftStartIso} onSaved={onConfirmed} />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function LateArrivalControl({
  booking, shiftStartIso, onSaved,
}: {
  booking: Application;
  shiftStartIso: string;
  onSaved: () => void;
}) {
  const logArrival = useServerFn(logBartenderArrival);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const pad = (n: number) => String(n).padStart(2, "0");
  const toLocal = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [value, setValue] = useState(() => toLocal(booking.actual_arrival_at ?? new Date().toISOString()));

  if (booking.actual_arrival_at) {
    const startTs = new Date(shiftStartIso).getTime();
    const arrivalTs = new Date(booking.actual_arrival_at).getTime();
    const lateMins = Math.max(0, Math.round((arrivalTs - startTs) / 60000));
    const deduction = (booking.late_deduction_pence ?? 0) / 100;
    const hourlyRate = Math.round((deduction / Math.max(lateMins, 1)) * 60 * 100) / 100;
    return (
      <div className="mt-2 border border-border bg-background/60 p-3 text-[0.7rem] tracking-wide-luxury">
        <p className="uppercase text-accent">Arrival logged</p>
        <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-muted-foreground">
          <dt>Scheduled start</dt><dd className="text-foreground/90 text-right">{new Date(shiftStartIso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</dd>
          <dt>Actual arrival</dt><dd className="text-foreground/90 text-right">{new Date(booking.actual_arrival_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</dd>
          <dt>Minutes late</dt><dd className="text-foreground/90 text-right">{lateMins} min</dd>
          {lateMins > 0 && (<>
            <dt>Pro-rata rate</dt><dd className="text-foreground/90 text-right">≈ £{hourlyRate.toFixed(2)}/hr</dd>
            <dt>Deducted from payout</dt><dd className="text-destructive font-medium text-right">−£{deduction.toFixed(2)}</dd>
          </>)}
        </dl>
      </div>
    );
  }

  const submit = async () => {
    setBusy(true);
    try {
      const res = await logArrival({ data: { booking_id: booking.id, actual_arrival_at: new Date(value).toISOString() } });
      if ("error" in res) throw new Error(res.error);
      toast.success(res.late_minutes > 0
        ? `Late by ${res.late_minutes} min — £${(res.deduction_pence / 100).toFixed(2)} will be deducted from payout.`
        : "Arrival logged on time. No deduction.");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not log arrival."); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[0.65rem] tracking-wide-luxury uppercase text-muted-foreground underline-offset-4 hover:text-accent hover:underline"
      >
        Log actual arrival time (for late deduction)
      </button>
    );
  }

  return (
    <div className="space-y-2 border border-border bg-background p-3">
      <Label htmlFor={`arr-${booking.id}`} className="text-[0.65rem] tracking-wide-luxury uppercase text-muted-foreground">
        Actual arrival time
      </Label>
      <Input
        id={`arr-${booking.id}`}
        type="datetime-local"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="rounded-none"
      />
      <p className="text-[0.65rem] text-muted-foreground">
        Pay is deducted pro-rata for minutes missed (contractor invoices reflect actual hours worked).
        Logged once — confirm carefully.
      </p>
      <div className="flex gap-2">
        <Button type="button" disabled={busy} onClick={submit} className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury text-xs">
          {busy ? "Saving…" : "Save arrival time"}
        </Button>
        <Button type="button" variant="outline" disabled={busy} onClick={() => setOpen(false)} className="rounded-none border-border tracking-wide-luxury text-xs">
          Cancel
        </Button>
      </div>
    </div>
  );
}

